import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MapIcon from '@mui/icons-material/Map';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Translate } from '../../../../../shared/LanguageService';
import { AddressLocation } from '../state/useAddressLocation';
import { ObjectType, PropertyAddress, PropertyInformation, PropertyOwner } from '../types';
import { AddressSearch, PlaceDetails } from './AddressSearch';

type PropertyInformationStepProps = {
    propertyInformation: PropertyInformation;
    objectTypes: ObjectType[];
    isCommercial: boolean;
    addressLocation: AddressLocation;
    map: React.ReactNode;
    debugMenu: React.ReactNode;
    onChange: (propertyInformation: PropertyInformation) => void;
    onChooseAddress: (place: PlaceDetails) => void;
    onRemovePickedLocation: () => void;
};

const twoColumns = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: '16px'
};
const section = { padding: '16px', display: 'grid', gap: '16px' };
const sectionHeading = { fontSize: '14px', fontWeight: 'bold' };

function LocationStatus({
    addressLocation,
    onRemovePickedLocation
}: {
    addressLocation: AddressLocation;
    onRemovePickedLocation: () => void;
}) {
    switch (addressLocation.status) {
        case 'searching':
            return (
                <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                    {Translate('order/create/v2/location/searching')}
                </Typography>
            );
        case 'found':
            return (
                <Alert severity="success" sx={{ fontSize: '14px' }}>
                    {Translate('order/create/v2/location/found')}
                </Alert>
            );
        case 'not-found':
            return (
                <Alert severity="warning" sx={{ fontSize: '14px' }}>
                    {Translate('order/create/v2/location/not-found')}
                </Alert>
            );
        case 'picked':
            return (
                <Alert
                    severity="info"
                    sx={{ fontSize: '14px', alignItems: 'center' }}
                    action={
                        <Button color="inherit" size="small" onClick={onRemovePickedLocation}>
                            {Translate('order/create/v2/location/remove-pick')}
                        </Button>
                    }
                >
                    {Translate('order/create/v2/location/picked')}
                </Alert>
            );
        default:
            return null;
    }
}

export function PropertyInformationStep({
    propertyInformation,
    objectTypes,
    isCommercial,
    addressLocation,
    map,
    debugMenu,
    onChange,
    onChooseAddress,
    onRemovePickedLocation
}: PropertyInformationStepProps) {
    let theme = useTheme();
    let isSmallerScreen = useMediaQuery(theme.breakpoints.down('sm'));
    let [isMapOpen, setIsMapOpen] = React.useState(false);
    let { owner, address, notes, extraDeliveryRecipients } = propertyInformation;
    let ownerHeadingKey = isCommercial
        ? 'orders/create/form/header/owner-commercial'
        : 'order/create/v2/step/property-information/owner';

    function changeOwner(field: keyof PropertyOwner, value: string) {
        onChange({ ...propertyInformation, owner: { ...owner, [field]: value } });
    }

    function changeAddress(field: keyof PropertyAddress, value: string | number | null) {
        onChange({ ...propertyInformation, address: { ...address, [field]: value } });
    }

    return (
        <Box sx={{ display: 'grid', gap: '16px' }}>
            <Paper
                component="section"
                variant="outlined"
                aria-labelledby="create-order-owner-heading"
                sx={section}
            >
                <Typography id="create-order-owner-heading" component="h2" sx={sectionHeading}>
                    {Translate(ownerHeadingKey)}
                </Typography>
                <Box sx={twoColumns}>
                    <TextField
                        id="create-order-owner-first-name"
                        label={Translate('orders/create/form/input-label/owner-first-name')}
                        value={owner.ownerFirstName}
                        onChange={(event) => changeOwner('ownerFirstName', event.target.value)}
                    />
                    <TextField
                        id="create-order-owner-last-name"
                        label={Translate('orders/create/form/input-label/owner-last-name')}
                        value={owner.ownerLastName}
                        onChange={(event) => changeOwner('ownerLastName', event.target.value)}
                    />
                    <TextField
                        id="create-order-owner-email-address"
                        type="email"
                        label={Translate('orders/create/form/input-label/owner-email-address')}
                        value={owner.ownerEmailAddress}
                        onChange={(event) => changeOwner('ownerEmailAddress', event.target.value)}
                    />
                    <TextField
                        id="create-order-owner-phone-number"
                        type="tel"
                        label={Translate(
                            'orders/create/form/input-label/owner-primary-phone-number'
                        )}
                        value={owner.ownerPrimaryPhoneNumber}
                        onChange={(event) =>
                            changeOwner('ownerPrimaryPhoneNumber', event.target.value)
                        }
                    />
                </Box>
            </Paper>
            <Paper
                component="section"
                variant="outlined"
                aria-labelledby="create-order-object-information-heading"
                sx={{ ...section, position: 'relative' }}
            >
                {debugMenu}
                <Typography
                    id="create-order-object-information-heading"
                    component="h2"
                    sx={sectionHeading}
                >
                    {Translate('order/create/v2/step/property-information/object-information')}
                </Typography>
                <AddressSearch onChoose={onChooseAddress} />
                <TextField
                    id="create-order-address-line-1"
                    required
                    fullWidth
                    label={Translate('orders/create/form/input-label/address-line-1')}
                    value={address.addressLine1}
                    onChange={(event) => changeAddress('addressLine1', event.target.value)}
                />
                <Box sx={twoColumns}>
                    <TextField
                        id="create-order-address-postal-number"
                        required
                        label={Translate('orders/create/form/input-label/address-postal-number')}
                        value={address.addressPostalNumber}
                        onChange={(event) =>
                            changeAddress('addressPostalNumber', event.target.value)
                        }
                    />
                    <TextField
                        id="create-order-address-city"
                        required
                        label={Translate('orders/create/form/input-label/address-city')}
                        value={address.addressCity}
                        onChange={(event) => changeAddress('addressCity', event.target.value)}
                    />
                </Box>
                <TextField
                    id="create-order-address-district"
                    fullWidth
                    label={Translate('orders/create/form/input-label/address-district')}
                    value={address.addressDistrict}
                    onChange={(event) => changeAddress('addressDistrict', event.target.value)}
                />
                <LocationStatus
                    addressLocation={addressLocation}
                    onRemovePickedLocation={onRemovePickedLocation}
                />
                {isSmallerScreen && (
                    <Button
                        variant="outlined"
                        startIcon={<MapIcon />}
                        onClick={() => setIsMapOpen(true)}
                    >
                        {Translate('order/create/v2/map/open')}
                    </Button>
                )}
                <TextField
                    id="create-order-object-type"
                    select
                    required
                    fullWidth
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    label={Translate('orders/create/form/input-label/object-type')}
                    value={address.objectType === null ? '' : String(address.objectType)}
                    onChange={(event) =>
                        changeAddress(
                            'objectType',
                            event.target.value === '' ? null : Number(event.target.value)
                        )
                    }
                >
                    <option value="">
                        {Translate('orders/create/form/select-object-type-placeholder')}
                    </option>
                    {objectTypes.map((objectType) => (
                        <option key={objectType.id} value={objectType.id}>
                            {Translate(objectType.name)}
                        </option>
                    ))}
                </TextField>
                <TextField
                    id="create-order-notes"
                    fullWidth
                    multiline
                    minRows={2}
                    label={Translate('orders/create/form/input-label/notes')}
                    helperText={Translate('order/create/v2/notes/helper-text')}
                    value={notes}
                    onChange={(event) =>
                        onChange({ ...propertyInformation, notes: event.target.value })
                    }
                />
                <TextField
                    id="create-order-extra-delivery-recipients"
                    fullWidth
                    multiline
                    minRows={2}
                    label={Translate('orders/create/form/input-label/extra-delivery-recipients')}
                    helperText={Translate(
                        'orders/create/form/input-helptext/extra-delivery-recipients'
                    )}
                    value={extraDeliveryRecipients}
                    onChange={(event) =>
                        onChange({
                            ...propertyInformation,
                            extraDeliveryRecipients: event.target.value
                        })
                    }
                />
            </Paper>
            <Dialog
                fullScreen
                open={isSmallerScreen && isMapOpen}
                onClose={() => setIsMapOpen(false)}
            >
                <DialogContent sx={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontSize: '14px', padding: '16px' }}>
                        {Translate('order/create/v2/map/hint')}
                    </Typography>
                    <Box sx={{ flex: 1 }}>{map}</Box>
                </DialogContent>
                <DialogActions sx={{ padding: '16px' }}>
                    <LocationStatus
                        addressLocation={addressLocation}
                        onRemovePickedLocation={onRemovePickedLocation}
                    />
                    <Button variant="contained" onClick={() => setIsMapOpen(false)}>
                        {Translate('order/create/v2/map/done')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
